'use client';

import { cn } from '@/lib/utils';
import { User } from 'lucide-react';

interface UserAvatarProps {
  /** Full display name (used for initials extraction). */
  displayName?: string | undefined;
  /** Optional image URL for the avatar. */
  imageUrl?: string | undefined;
  /** Size variant. */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes. */
  className?: string;
}

const sizeClasses = {
  sm: 'h-7 w-7 text-xs',
  md: 'h-8 w-8 text-xs',
  lg: 'h-10 w-10 text-sm',
} as const;

const iconSizes = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
} as const;

/**
 * Extracts up to 2 initials from a display name.
 * Falls back to null if the name is empty.
 */
function getInitials(name: string | undefined): string | null {
  if (!name?.trim()) return null;
  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Renders a circular user avatar.
 * Shows the user's profile image if available, otherwise shows initials,
 * and falls back to a generic User icon.
 */
export function UserAvatar({
  displayName,
  imageUrl,
  size = 'md',
  className,
}: UserAvatarProps) {
  const initials = getInitials(displayName);

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={displayName || 'User avatar'}
        className={cn(
          'rounded-full object-cover',
          sizeClasses[size],
          className
        )}
      />
    );
  }

  return (
    <span
      className={cn(
        'flex items-center justify-center rounded-full bg-brand-tertiary text-white font-medium select-none',
        sizeClasses[size],
        className
      )}
      aria-hidden='true'
    >
      {initials || <User className={iconSizes[size]} />}
    </span>
  );
}
